import React, { useState } from 'react';
import { BOOKING_URL } from '../constants/config';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
  Theme, // Import Theme type for useTheme hook
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

// Import your carousel components
// Ensure these paths are correct for your project structure
import VerticalImageCarousel from '../components/VerticalImageCarousel';
import HorizontalImageCarousel from '../components/HorizontalImageCarousel';

import { motion } from 'framer-motion';
import DentalLogo from '../components/DentalLogoOnly';
import { useAppLanguage } from '../hooks/useAppLanguage'; // Correctly imported
import { useSectionHash } from '../hooks/useSectionHash';

// Path to your uploaded image - replace with the correct way to access your image
// For example, if it's in public folder: const userUploadedImagePath = '/image_dd09c5.jpg';
// Or if you import it: import userImage from './path/to/image_dd09c5.jpg';

// Images for the carousels
const carouselDisplayImages: string[] = [
  // userUploadedImagePath, // Your uploaded image first
  '/img/clinic/building_new.jpg',
  '/img/clinic/outdoor_new.jpg',
  '/img/clinic/front_office_new.jpg',
  'img/clinic/waiting_area_new.jpg',
  '/img/clinic/cleaning_station_1_new.jpg',
  '/img/clinic/cleaning_station_2_new.jpg',
  'img/clinic/op1_new.jpg',
  '/img/clinic/op3_new.jpg',
  '/img/clinic/op4_new.jpg',
  '/img/clinic/cbct_overview_new.jpg',
  '/img/clinic/cbct.jpg',
  '/img/clinic/itero.jpg',
];

const Home: React.FC = () => {
  const theme: Theme = useTheme();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, currentLanguage, setLanguage } = useAppLanguage();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (language: string) => {
    setLanguage(language);
    handleClose();
  };

  useSectionHash();

  return (
    <Box id="home" sx={{ pt: { xs: '64px', sm: '80px' } }}>
      {/* Blue Background Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 4, sm: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          minHeight: { xs: 'auto', sm: 'calc(100vh - 80px)' },
          mt: { xs: 0, sm: 0 },
        }}
      >
        <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 0 } }}>
          <Grid container spacing={{ xs: 2, sm: 3, md: 5 }} alignItems="center" justifyContent="center">
            {/* Left Column: Text + Button */}
            <Grid item xs={12} sm={7} md={6}>
              <motion.div
                initial={{ opacity: 0, y: isMobile ? 20 : 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  textAlign: isMobile ? 'center' : 'left',
                }}
              >
                {/* Logo and Slogan Container */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  mb: isMobile ? 2 : 4,
                }}>
                  {/* Slogan Text */}
                  <Typography
                    variant={isMobile ? "h3" : "h1"}
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      background: 'linear-gradient(145deg, #ffffff 70%, #dddddd 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      lineHeight: 1.2,
                      textAlign: isMobile ? 'center' : 'left',
                      whiteSpace: 'pre-line',
                      fontFamily: '"Reenie Beanie", serif',
                      letterSpacing: '0.02em',
                      flex: 1,
                    }}
                  >
                    {t('home.slogan', 'Your Smile\nOur Priority')}
                  </Typography>

                  {/* Logo */}
                  {isMobile && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                      <DentalLogo sx={{
                        fontSize: 110,
                        opacity: 0.9,
                        filter: 'drop-shadow(0 0 10px rgba(245, 241, 241, 0.2))',
                        color: 'white',
                        ml: 2,
                      }} />
                    </motion.div>
                  )}
                </Box>

                {/* Button - NOW TRANSLATED */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: isMobile ? 'center' : 'flex-start',
                  mt: { xs: 2, sm: 2, md: 4 },
                }}>
                  <Button
                    variant="contained"
                    size={isMobile ? "small" : "large"}
                    onClick={() => window.location.href = BOOKING_URL}
                    sx={{
                      px: { xs: 3, md: 5 },
                      py: { xs: 1, md: 1.8 },
                      background: 'linear-gradient(45deg, #f0f0f0 30%, #ffffff 90%)',
                      boxShadow: '0 4px 10px 2px rgba(0, 0, 0, 0.15)',
                      color: theme.palette.primary.dark,
                      fontWeight: 'bold',
                      borderRadius: '12px',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #e0e0e0 30%, #f5f5f5 90%)',
                        transform: 'translateY(-3px) scale(1.03)',
                        boxShadow: '0 6px 12px 3px rgba(0, 0, 0, 0.2)',
                        color: theme.palette.primary.main,
                      },
                      transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    }}
                  >
                    {t('home.bookAppointment', 'Book Appointment')}
                  </Button>
                </Box>

                {/* Descriptive Paragraph - Hidden on extra-small screens - NOW TRANSLATED */}
                {/* <Typography
                  variant={isMobile ? "body1" : "h5"}
                  paragraph
                  sx={{
                    textAlign: isMobile ? 'center' : 'left',
                    my: { sm: 2, md: 3 },
                    opacity: 0.9,
                    lineHeight: 1.6,
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  {t('home.description')}
                </Typography> */}
              </motion.div>
            </Grid>

            {/* Right Column: Image Carousel - Only show on desktop */}
            {!isMobile && (
              <Grid item xs={12} sm={5} md={6} sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pl: { sm: 2, md: 4 },
                height: { sm: '400px', md: '100%' },
                minHeight: { sm: '350px', md: '450px' },
                maxHeight: { md: '550px' },
              }}>
                <Box sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                }}>
                  <VerticalImageCarousel
                    images={carouselDisplayImages}
                    imageHeight={300}
                    scrollDuration={70}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Image Carousel Section - Only show on mobile */}
      {isMobile && (
        <Box sx={{
          py: { xs: 4, sm: 6 },
          bgcolor: 'background.paper'
        }}>
          <Container maxWidth="lg">
            <Grid container spacing={3} alignItems="center" justifyContent="center">
              <Grid item xs={12}>
                <Box sx={{
                  width: '100%',
                  height: '170px',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                }}>
                  <HorizontalImageCarousel
                    images={carouselDisplayImages}
                    imageWidth={300}
                    imageHeight={170}
                    scrollDuration={80}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}

      {/* Floating Language Switch Widget */}
      <Button
        variant="contained"
        color="inherit"
        aria-label="switch language"
        onClick={handleClick}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          borderRadius: '12px',
          minWidth: 'auto',
          px: 2,
          py: 1,
          bgcolor: 'white',
          color: 'text.primary',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            bgcolor: 'grey.100',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span style={{ fontSize: '16px' }}>
            {currentLanguage === 'en' ? '🇺🇸' : '🇨🇳'}
          </span>
          <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
            {currentLanguage === 'en' ? 'EN' : 'ZH'}
          </span>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <MenuItem onClick={() => handleLanguageSelect('en')} sx={{ minWidth: '120px' }}>
          <ListItemIcon>
            <span style={{ fontSize: '20px' }}>🇺🇸</span>
          </ListItemIcon>
          <ListItemText primary="English" />
        </MenuItem>
        <MenuItem onClick={() => handleLanguageSelect('zh')} sx={{ minWidth: '120px' }}>
          <ListItemIcon>
            <span style={{ fontSize: '20px' }}>🇨🇳</span>
          </ListItemIcon>
          <ListItemText primary="中文" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Home;
